import Phaser from 'phaser';
import type { InboxGame, InboxResponse } from '../../api/inbox-types.js';
import { MAPS } from '../../maps/index.js';
import { apiBaseUrl } from '../../platform/index.js';
import {
  getAuth,
  onAuthChange,
  resendConfirmation,
  signOut,
  type AuthState,
} from '../../store/auth.js';
import {
  getProfile,
  onProfileChange,
  type ProfileState,
} from '../../store/profile.js';
import {
  createBetaGame,
  type BetaGameMode,
  type BetaGameConfig,
} from '../../store/createBetaGame.js';
import { cancelInvite } from '../../store/invites.js';
import { attachBottomNav, preloadNavAssets } from '../view/AppNav.js';
import { BetaConfigModal } from '../view/BetaConfigModal.js';
import { Button } from '../view/Button.js';
import {
  drawInboxGameCard,
  inboxGameBucketRank,
} from '../view/InboxGameCard.js';
import { navigateTo } from '../sceneNav.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// --- Decorative die geometry ---
// Ghost dice in the background — outline only, no opaque face, drawn
// in muted blue-gray at low container alpha so they read as a quiet
// motif rather than the loud white tiles in the in-game tray. Sized
// up a touch since they're decoration, not gameplay.
const DIE_SIZE = 84;
const DIE_CORNER_RADIUS = 11;
const DIE_STROKE_WIDTH = 3;
// Per-element alphas. Dice get a muted gray fill at moderate alpha
// so they read as soft tile shapes against the very dark page
// background. Stroke and pips stay strong enough to read at a
// glance. A dark-navy fill blended away against the canvas color;
// gray actually contrasts.
const DIE_FILL_ALPHA = 0.55;
const DIE_STROKE_ALPHA = 0.75;
const DIE_PIP_ALPHA = 0.85;
const DIE_FILL_INT = 0x8a96a8; // muted blue-gray, palette-friendly
const PIP_RADIUS = 5;
const PIP_OFFSET = 22;

// Page background — matches the logo's own canvas color so the logo
// blends seamlessly into the scene rather than sitting on top of a
// slightly different navy.
const HOME_BG_INT = 0x040713;

// Hex grid vertical fade. The grid is fully opaque at the canvas
// midpoint (y=640 = 50% up from bottom) and ramps to invisible at
// y=384 (70% up from bottom = 30% from top). Going UP through the
// upper half of the screen, hexes get progressively fainter — kills
// the seam where the grid used to butt against the logo.
const HEX_FADE_START_Y = 384;
const HEX_FADE_END_Y = 640;
const HEX_FULL_ALPHA = 0.6;

const FACE_LAYOUT: Readonly<Record<number, readonly [number, number][]>> = {
  3: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [0, 0],
    [PIP_OFFSET, PIP_OFFSET],
  ],
  5: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [PIP_OFFSET, -PIP_OFFSET],
    [0, 0],
    [-PIP_OFFSET, PIP_OFFSET],
    [PIP_OFFSET, PIP_OFFSET],
  ],
};

// --- Decorative reticle geometry ---
// Same scope-style ring as CombatVfx, just dimmer. Two reticles in
// opposite corners hint at the combat motif without dominating the
// home screen.
const RETICLE_OUTER_R = 64;
const RETICLE_INNER_R = 32;
const RETICLE_TICK_OUTER = 78;
const RETICLE_TICK_INNER = 66;
const RETICLE_ALPHA = 0.22;

/**
 * Launch screen. Renders the BattleShark logo plus light decorative
 * graphics (target reticles, two dice) and the two mode buttons:
 *
 *  - "PLAY VS AI"     → switches to the Game scene
 *  - "PLAY VS HUMAN"  → disabled placeholder with a COMING SOON note
 *
 * Same hex-grid backdrop as the in-game canvas so the screens read as
 * one product. Logo asset is `logo.png` (the home variant) — the
 * in-game HUD continues to use `logo2.png`.
 */
// Default map preselected in the beta config modal. Slash is mid-sized
// (more interesting than Ring at 3-4 players, faster than Terra), and
// the user picked it as the new default when the modal was introduced.
const DEFAULT_BETA_MAP_ID = 'slash';

export class HomeScene extends Phaser.Scene {
  // Guards against double-tap on a beta button during the in-flight
  // POST /api/games. Once a beta game is in the process of being
  // created we lock everything else out — the request settles in
  // under a second on a normal connection.
  private creatingBeta = false;
  private statusText: Phaser.GameObjects.Text | null = null;
  private configModal: BetaConfigModal | null = null;

  // Auth control state. Container holds whichever auth chip is
  // currently rendered; onAuthChange + onProfileChange swap its
  // contents when either transitions. Unsubscribes run on scene
  // shutdown. The `| undefined` (not `?:`) is required under
  // exactOptionalPropertyTypes so we can reassign back to undefined.
  private authContainer: Phaser.GameObjects.Container | null = null;
  private unsubscribeAuth: (() => void) | undefined = undefined;
  private unsubscribeProfile: (() => void) | undefined = undefined;

  // MP-gate state. The two beta buttons (FFA + Team Up vs AI) get
  // disabled when MP isn't available (signed out, email not
  // confirmed, etc.). Banner sits above them explaining the reason
  // and offering an action (Resend, etc.) where appropriate.
  private mpButtons: Button[] = [];
  private gateBannerContainer: Phaser.GameObjects.Container | null = null;
  private gateBannerStatus: Phaser.GameObjects.Text | null = null;

  // Inline-inbox state. Populated by an async fetch on scene start
  // when the user is signed in + email-confirmed. Most-recent active
  // game surfaces as a card between the logo and the buttons so
  // returning users can rejoin in one tap.
  private inlineInboxContainer: Phaser.GameObjects.Container | null = null;

  constructor() {
    super('Home');
  }

  preload(): void {
    preloadNavAssets(this);
    // Distinct key from GameScene's 'logo' so the two scenes can use
    // different image files without preload collisions. logomain.jpg
    // is the high-res stacked composition (mascot + wordmark + tagline)
    // baked onto the brand-dark canvas — JPEG keeps the file lightweight
    // (88 KB vs 2 MB PNG) and the dark backdrop blends seamlessly with
    // the page bg, so no transparency is needed.
    this.load.image('homeLogo', '/assets/logomain.jpg');
  }

  create(): void {
    // Reset per-instance state. scene.start('Home') re-runs create()
    // but Phaser keeps the instance, so stale field refs from a prior
    // mount would leak.
    this.creatingBeta = false;
    this.statusText = null;
    this.configModal = null;
    this.authContainer = null;
    this.mpButtons = [];
    this.gateBannerContainer = null;
    this.gateBannerStatus = null;
    this.inlineInboxContainer = null;

    // Auto-route to username setup BEFORE rendering when the user is
    // signed in but has no profile row. Skipping render this turn
    // avoids a flicker of "email · SIGN OUT" before the route fires.
    // Profile state may still be 'loading' at this point — in that
    // case we render normally and let the listener (below) catch the
    // 'missing' transition when the fetch resolves.
    if (this.shouldRouteToUsernameSetup()) {
      this.scene.start('UsernameSetup');
      return;
    }

    this.drawBackground();
    this.drawHexGrid();
    // Bottom-left reticle goes behind the logo (it's well below the
    // logo anyway). Top-right reticle is drawn AFTER the logo so it
    // sits in front, overlaying the logo's right side as a tactical
    // accent.
    this.drawCornerReticle(90, DESIGN_HEIGHT - 240);
    // Ghost dice are decoration — render BEFORE the logo so a tall
    // logo would always paint over them in the rare case the bounds
    // overlap.
    this.drawDecorativeDice();
    this.drawLogo();
    // Top-right corner reticle removed — would overlap the new
    // larger logo. Bottom-left reticle (90, 1040) stays for the
    // tactical decoration motif alongside the ghost dice.
    this.drawGateBanner();
    this.drawButtons();
    this.drawIconButtons();
    this.drawAuthControl();
    // Apply gate state after both buttons + banner exist; the
    // listeners in drawAuthControl will keep them in sync from here.
    this.refreshGate();
    // Bottom AppNav strip (5-icon shared chrome). Active tab = home.
    // Tapping 'games' jumps to InboxScene, 'settings' (the gear,
    // far right) opens the SettingsScene/HowTo/About group.
    // Replaces the old standalone CogButton + preview inbox link.
    attachBottomNav(this, 'home');
    // Async-load the inline inbox preview. Skips entirely when
    // gated (signed-out / unconfirmed); otherwise drops the most
    // recent active game between the logo and the button stack.
    void this.loadInlineInbox();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeAuth?.();
      this.unsubscribeAuth = undefined;
      this.unsubscribeProfile?.();
      this.unsubscribeProfile = undefined;
    });
  }

  /**
   * True iff the user has a session but no profile row. Used both
   * at scene-start (synchronous check) and inside the profile-change
   * listener (so a late-arriving 'missing' verdict bounces them out
   * to the setup flow even after Home is already rendered).
   */
  private shouldRouteToUsernameSetup(): boolean {
    const auth = getAuth();
    const profile = getProfile();
    return auth.user !== null && profile.kind === 'missing';
  }

  // --- Background + grid ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(HOME_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  private drawHexGrid(): void {
    // Flat-top hex pattern with a vertical alpha fade — invisible at
    // the top, full alpha by HEX_FADE_END_Y. The fade kills the hard
    // seam where the grid used to butt against the logo.
    const r = 28;
    const dx = r * 1.5;
    const dy = r * Math.sqrt(3);
    const cos60 = r / 2;
    const sin60 = (r * Math.sqrt(3)) / 2;
    const fadeRange = HEX_FADE_END_Y - HEX_FADE_START_Y;

    const g = this.add.graphics();

    let col = 0;
    for (let cx = -r; cx <= DESIGN_WIDTH + r; cx += dx, col++) {
      const offsetY = col % 2 === 0 ? 0 : dy / 2;
      for (let cy = -r + offsetY; cy <= DESIGN_HEIGHT + dy; cy += dy) {
        if (cy < HEX_FADE_START_Y) continue;
        const t = Math.min(1, (cy - HEX_FADE_START_Y) / fadeRange);
        const alpha = t * HEX_FULL_ALPHA;
        g.lineStyle(1, COLORS.hexGridInt, alpha);
        g.beginPath();
        g.moveTo(cx + r, cy);
        g.lineTo(cx + cos60, cy - sin60);
        g.lineTo(cx - cos60, cy - sin60);
        g.lineTo(cx - r, cy);
        g.lineTo(cx - cos60, cy + sin60);
        g.lineTo(cx + cos60, cy + sin60);
        g.closePath();
        g.strokePath();
      }
    }
  }

  // --- Decorations ---

  private drawCornerReticle(cx: number, cy: number): void {
    const g = this.add.graphics();
    const RED = COLORS.attackTargetStrokeInt;

    g.lineStyle(3, RED, RETICLE_ALPHA);
    g.strokeCircle(cx, cy, RETICLE_OUTER_R);

    g.lineStyle(2, RED, RETICLE_ALPHA);
    g.strokeCircle(cx, cy, RETICLE_INNER_R);

    g.lineStyle(3, RED, RETICLE_ALPHA);
    const tickAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    for (const a of tickAngles) {
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      g.beginPath();
      g.moveTo(cx + ux * RETICLE_TICK_OUTER, cy + uy * RETICLE_TICK_OUTER);
      g.lineTo(cx + ux * RETICLE_TICK_INNER, cy + uy * RETICLE_TICK_INNER);
      g.strokePath();
    }
  }

  private drawLogo(): void {
    const logo = this.add.image(DESIGN_WIDTH / 2, 0, 'homeLogo');
    // logomain.jpg is the home-screen focal point (1000×747 source
    // after optimization). Designed to fill the full canvas width —
    // the side art bleeds to the edge of the screen so there's no
    // dark margin between the logo and the device bezel. At 720
    // wide the height comes out to ~538px (1.34:1 aspect ratio).
    const targetWidth = DESIGN_WIDTH;
    const scale = targetWidth / logo.width;
    logo.setScale(scale);
    const logoH = logo.height * scale;
    // Logo top sits flush at y=0 — the JPG has a hard edge at the
    // top of its hex-backdrop, and pinning it to the canvas top
    // hides that seam behind the device's top safe area / status
    // bar instead of leaving a visible horizontal line mid-screen.
    // Bottom now lands around y=538.
    logo.setY(logoH / 2);
  }

  private drawDecorativeDice(): void {
    // Two ghost dice tucked near the lower outer edges, sitting in
    // the same vertical band as the button column (y=790/900/1010)
    // but pushed wide so they read as quiet background motifs —
    // a modern hint at "this is a dice game" without shouting.
    this.drawDie(110, 750, 3, -10);
    this.drawDie(DESIGN_WIDTH - 110, 790, 5, 10);
  }

  /**
   * Draw a single ghost die at (cx, cy) showing the given face, rotated
   * by `tiltDegrees` for a hand-tossed feel. Semitransparent dark fill
   * inside, more-visible outline stroke + pip dots on top — reads as
   * a quiet tile shape rather than a hollow wireframe.
   */
  private drawDie(
    cx: number,
    cy: number,
    face: number,
    tiltDegrees: number
  ): void {
    const container = this.add.container(cx, cy);

    const body = this.add.graphics();
    // Muted blue-gray fill so the die has a body that visibly reads
    // against the dark page background. Dark-navy fills blended into
    // the canvas; gray gives actual contrast.
    body.fillStyle(DIE_FILL_INT, DIE_FILL_ALPHA);
    body.fillRoundedRect(
      -DIE_SIZE / 2,
      -DIE_SIZE / 2,
      DIE_SIZE,
      DIE_SIZE,
      DIE_CORNER_RADIUS
    );
    body.lineStyle(DIE_STROKE_WIDTH, COLORS.borderBrightInt, DIE_STROKE_ALPHA);
    body.strokeRoundedRect(
      -DIE_SIZE / 2,
      -DIE_SIZE / 2,
      DIE_SIZE,
      DIE_SIZE,
      DIE_CORNER_RADIUS
    );

    const pips = this.add.graphics();
    pips.fillStyle(COLORS.textSecondaryInt, DIE_PIP_ALPHA);
    const positions = FACE_LAYOUT[face] ?? [];
    for (const [dx, dy] of positions) {
      pips.fillCircle(dx, dy, PIP_RADIUS);
    }

    container.add([body, pips]);
    container.setRotation(Phaser.Math.DegToRad(tiltDegrees));
  }

  // --- Buttons ---

  private drawButtons(): void {
    // Three buttons stacked vertically. PLAY VS AI is the stable
    // single-player flow → opens the full lobby (maps, personalities,
    // teams). The two beta buttons each create a fixed-config online
    // game and route to /game/:id, no setup required. BETA stickers on
    // the multiplayer buttons signal "this is testing scope".
    const cx = DESIGN_WIDTH / 2;
    // Card sits at y=528–684 with the "+ N more games" link at
    // y=698 when there are 2+ games. Button center y=790 means
    // top y=750 → ~45px of clear space below the link bottom and
    // ~66px below the card bottom when no link is shown. Status
    // text still has ~100px clearance above the bottom AppNav.
    const aiY = 790;
    const onlineY = 900;
    const teamY = 1010;

    new Button(this, {
      x: cx,
      y: aiY,
      width: 320,
      height: 80,
      text: 'Play vs AI',
      style: 'primary',
      // Hand off to the lobby — that's where map / seats / personalities
      // / teams get configured before the game starts. SP isn't gated
      // by auth; keep this button enabled in every state.
      onTap: () => this.scene.start('Lobby'),
    });

    // Collect refs to the two MP buttons so refreshGate() can flip
    // them enabled/disabled as auth state changes.
    this.mpButtons.push(
      this.drawBetaButton({
        x: cx,
        y: onlineY,
        text: 'Free-for-All',
        mode: 'ffa',
      })
    );

    this.mpButtons.push(
      this.drawBetaButton({
        x: cx,
        y: teamY,
        text: 'Team Up vs AI',
        mode: '2v2-ai',
      })
    );

    // Inline status line under the beta buttons. Empty in steady state;
    // shows "Creating game…" while a POST is in flight, or an error
    // message if the create call fails. Sits above the InfoButton.
    this.statusText = this.add
      .text(cx, teamY + 64, '', {
        fontFamily: FONT_HEADER,
        fontSize: '14px',
        color: COLORS.accentAmberHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    // "Browse Open Games →" CTA below the status line. Subtle text-
    // link styled like the "+ N more games" link on the inbox card —
    // doesn't compete with the main buttons but gives a one-tap entry
    // into the Inbox LOBBY tab for players who want to discover
    // lobbies. (The standalone PublicGamesScene was retired once the
    // LOBBY tab landed inline in InboxScene.)
    const browseLink = this.add
      .text(cx, teamY + 110, 'Browse Open Games  →', {
        fontFamily: FONT_MONO,
        fontSize: '14px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    browseLink.on('pointerup', () =>
      navigateTo(this, 'Inbox', { initialTab: 'lobby' })
    );
  }

  /**
   * Draws one of the two beta multiplayer buttons (1v1 / 2v2 vs AI),
   * with a small "BETA" sticker overlapping the top-right corner so
   * testers know these are part of the multiplayer testing scope.
   * onTap creates the game on the server then hands off to JoinScene.
   */
  private drawBetaButton(opts: {
    x: number;
    y: number;
    text: string;
    mode: BetaGameMode;
  }): Button {
    const width = 320;
    const height = 80;

    const btn = new Button(this, {
      x: opts.x,
      y: opts.y,
      width,
      height,
      text: opts.text,
      onTap: () => {
        this.openConfigModal(opts.mode);
      },
    });

    // BETA sticker — small amber pill sitting at the top-right corner
    // of the button, offset outward so it reads as a tag rather than
    // part of the label. Container so the bg + text shift together if
    // we ever animate or reposition.
    const pillWidth = 58;
    const pillHeight = 22;
    const pillX = opts.x + width / 2 - pillWidth / 2 + 14;
    const pillY = opts.y - height / 2 + 2;
    const sticker = this.add.container(pillX, pillY);

    const pillBg = this.add.rectangle(
      0,
      0,
      pillWidth,
      pillHeight,
      COLORS.accentAmberInt
    );
    pillBg.setStrokeStyle(1, COLORS.bgDeepInt, 0.9);

    const pillText = this.add
      .text(0, 0, 'BETA', {
        fontFamily: FONT_HEADER,
        fontSize: '12px',
        color: COLORS.bgDeepHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    sticker.add([pillBg, pillText]);
    // Keep the sticker above the button stroke so it isn't clipped.
    sticker.setDepth(10);

    return btn;
  }

  /**
   * Open the beta config modal so the host can pick map + (for 2v2-ai)
   * AI personalities before the game is created. The modal calls back
   * via onCreate(selection) once they tap CREATE & SHARE.
   */
  private openConfigModal(mode: BetaGameMode): void {
    if (this.creatingBeta || this.configModal) return;
    this.statusText?.setText('');

    this.configModal = new BetaConfigModal(this, {
      mode,
      maps: MAPS,
      defaultMapId: DEFAULT_BETA_MAP_ID,
      // Default personality pairing keeps variety: aggressive opens
      // hot, cautious holds back — gives the team game two different
      // play styles to face. Host can override either.
      defaultPersonalities: ['aggressive', 'cautious'],
      onCancel: () => {
        this.configModal = null;
      },
      onCreate: (selection) => {
        void this.handleBetaCreate(mode, {
          mapId: selection.mapId,
          aiPersonalities: selection.aiPersonalities,
          isPublic: selection.isPublic,
          targetSeats: selection.targetSeats,
        });
      },
    });
  }

  /**
   * Handle CREATE & SHARE from the config modal: lock the home, POST
   * to create the game, push the URL into history so the browser
   * back button works naturally, then start JoinScene with the id.
   * Leaves the modal open if creation fails so the host can retry
   * without re-picking their settings.
   */
  private async handleBetaCreate(
    mode: BetaGameMode,
    config: BetaGameConfig
  ): Promise<void> {
    if (this.creatingBeta) return;
    this.creatingBeta = true;
    this.statusText?.setColor(COLORS.accentAmberHex);
    this.statusText?.setText('Creating game…');
    try {
      const { id } = await createBetaGame(mode, config);
      // Update the URL so the address bar reflects the joinable game
      // and the host can copy/paste it directly. No reload — we just
      // hand off to JoinScene via Phaser's scene manager.
      window.history.pushState({}, '', `/game/${id}`);
      this.configModal?.destroy();
      this.configModal = null;
      // navigateTo (not scene.start) so JoinScene's Back returns
      // here to Home rather than falling back via empty history.
      navigateTo(this, 'Join', { gameId: id });
    } catch (err) {
      console.error('createBetaGame failed', err);
      this.statusText?.setColor(COLORS.accentRedHex);
      this.statusText?.setText('Could not create game. Try again.');
      this.creatingBeta = false;
    }
  }

  private drawIconButtons(): void {
    // Standalone Settings button removed in MP-Lobby-2.0 A.4 — the
    // bottom AppNav strip's 'profile' slot now serves the same
    // entry point into the Settings/HowTo/About group. Kept the
    // method (no-op) so the create() call sequence reads cleanly
    // for callers that wired in here previously.
  }

  // --- Inline inbox preview ---

  /**
   * Fetch the user's game list and surface the most-recent active
   * (or lobby) game as a card on the home screen. Best-effort —
   * silently skips when the user is signed-out, unconfirmed, or the
   * call fails. The full list lives at InboxScene (AppNav 'games').
   */
  private async loadInlineInbox(): Promise<void> {
    const auth = getAuth();
    if (!auth.session || !auth.user) return;
    // Skip if email isn't confirmed yet — the gate banner is up and
    // owns this mid-screen real estate.
    if (!auth.user.email_confirmed_at) return;

    try {
      const res = await fetch(`${apiBaseUrl()}/api/games`, {
        headers: { Authorization: `Bearer ${auth.session.access_token}` },
      });
      if (!res.ok) return;
      const body = (await res.json()) as InboxResponse;
      const userId = auth.user.id;
      this.renderInlineInbox(body.games, userId);
    } catch {
      // Best-effort — a network blip on home shouldn't be a UI error.
    }
  }

  private renderInlineInbox(
    games: readonly InboxGame[],
    meUserId: string
  ): void {
    // Filter to active + lobby only (past games belong in the full
    // InboxScene, not on home). Sort by inbox bucket rank (YOUR TURN
    // first), then by recency.
    const sortable = games.filter((g) => g.status !== 'finished');
    if (sortable.length === 0) return;
    const sorted = [...sortable].sort((a, b) => {
      const aRank = inboxGameBucketRank(a);
      const bRank = inboxGameBucketRank(b);
      if (aRank !== bRank) return aRank - bRank;
      return b.lastActionAt.localeCompare(a.lastActionAt);
    });

    // The card is intentionally positioned to overlap the very
    // bottom of the full-width logo by ~10px — the logo art was
    // designed with that overlap zone, so the card's top edge
    // tucking under the logo's hex backdrop reads as one unified
    // composition. Logo ends at ~y=538; card top sits at y=510.
    // YOUR GAMES header sits ON the logo (~y=485) since the logo's
    // bottom band is dark hex pattern, not the focal shark art.
    // Nudged up vs. the original 503/528 so the taller (168px) card
    // leaves clear breathing room above the "+ N more games" link.
    const headerY = 477;
    const cardY = 502;
    const cardWidth = DESIGN_WIDTH - 36 * 2;

    this.inlineInboxContainer = this.add.container(0, 0);

    // "YOUR GAMES" label — small mono, muted, letter-spaced so it
    // reads as a section header rather than competing content.
    const header = this.add
      .text(DESIGN_WIDTH / 2, headerY, 'YOUR GAMES', {
        fontFamily: FONT_MONO,
        fontSize: '14px',
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5);
    this.inlineInboxContainer.add(header);

    const top = sorted[0];
    if (!top) return;
    const cardResult = drawInboxGameCard({
      scene: this,
      parent: this.inlineInboxContainer,
      x: 36,
      y: cardY,
      width: cardWidth,
      game: top,
      meUserId,
      onTap: (game) => this.openGame(game.id),
      onDecline: (game) => void this.handleDecline(game),
    });

    // "+ N more games →" link below the card, tappable to the full
    // InboxScene. Hidden when there's only one game. Uses the card's
    // actual returned height (not a hardcoded literal) so it stays
    // correct if InboxGameCard's CARD_HEIGHT changes again.
    const remaining = sorted.length - 1;
    if (remaining > 0) {
      const linkY = cardY + cardResult.height + 21;
      const link = this.add
        .text(
          DESIGN_WIDTH / 2,
          linkY,
          `+ ${remaining} more game${remaining === 1 ? '' : 's'}  →`,
          {
            fontFamily: FONT_MONO,
            fontSize: '14px',
            color: COLORS.accentCyanHex,
            fontStyle: '700',
          }
        )
        .setLetterSpacing(2)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      link.on('pointerup', () => navigateTo(this, 'Inbox'));
      this.inlineInboxContainer.add(link);
    }
  }

  private openGame(gameId: string): void {
    // navigateTo so JoinScene's Back returns here (Home) — without
    // it the history stack would be empty and Back would still
    // fall back to Home anyway, but going through the stack keeps
    // the nav model consistent for future chains.
    window.history.pushState({}, '', `/game/${gameId}`);
    navigateTo(this, 'Join', { gameId });
  }

  private async handleDecline(game: InboxGame): Promise<void> {
    if (game.invitedSeatIndex == null) {
      console.warn('decline: invitedSeatIndex missing', game.id);
      return;
    }
    const result = await cancelInvite(game.id, game.invitedSeatIndex);
    // 404 = row already gone (e.g. double-tap, or host cancelled
    // first). Desired end state already true — silent success.
    if (!result.ok && result.status !== 404) {
      console.warn('decline failed', {
        gameId: game.id,
        seatIndex: game.invitedSeatIndex,
        error: result.error,
        status: result.status,
      });
    }
    // Refresh the inline-inbox preview so the declined invite drops
    // off the list. The card list re-renders inside renderInlineInbox.
    void this.loadInlineInbox();
  }

  // --- Gate banner (Slice 1.2c) ---

  /**
   * Reasons MP might be locked. Returned by mpBlockReason() to drive
   * both the banner copy and button-enabled state.
   *
   *   - 'signin'   — no auth user. SIGN IN chip in top-right is the action.
   *   - 'confirm'  — auth user exists but email_confirmed_at is null.
   *                  RESEND link lives in the banner.
   *   - 'username' — auth user + confirmed but profile.kind !== 'exists'.
   *                  This should be momentary in practice (HomeScene
   *                  auto-routes to UsernameSetup on 'missing'), but
   *                  the brief loading window is still gated.
   */
  private mpBlockReason(): 'signin' | 'confirm' | 'username' | null {
    const auth = getAuth();
    const profile = getProfile();
    if (!auth.user) return 'signin';
    if (!auth.user.email_confirmed_at) return 'confirm';
    if (profile.kind !== 'exists') return 'username';
    return null;
  }

  /**
   * Create the banner shell once. refreshGate() flips visibility + copy
   * + the optional RESEND action based on the current reason. Sits
   * between the logo (ends ~y=480) and the first MP button (y=870),
   * giving the user reading order: brand → status hint → action buttons.
   */
  private drawGateBanner(): void {
    const cx = DESIGN_WIDTH / 2;
    // Sits between the (full-width) logo bottom at ~y=538 and the
    // first MP button at y=760. Center at y=620, banner top at
    // ~y=572 → 34px gap from logo, 92px gap to first button. The
    // gate banner is only shown when signed out (mutually exclusive
    // with the inline inbox card), so it doesn't compete with
    // the YOUR GAMES card for the same vertical band.
    const y = 620;
    const width = 640;
    const height = 96;

    this.gateBannerContainer = this.add.container(cx, y);

    // Amber-bordered card. Slightly elevated bg so it reads as a
    // surface, not a tint of the canvas. Border + text colors all
    // come from the existing amber accent so it slots into the
    // palette cleanly.
    const bg = this.add.rectangle(0, 0, width, height, COLORS.bgPanelInt);
    bg.setStrokeStyle(1, COLORS.accentAmberInt, 0.85);

    const title = this.add
      .text(0, -20, '', {
        fontFamily: FONT_HEADER,
        fontSize: '15px',
        color: COLORS.accentAmberHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);
    title.setName('title');

    // Body line + RESEND link share the row. statusText carries the
    // current state ("Tap RESEND…", "Sent! Check your inbox.", etc.)
    // — refreshGate() rebuilds the action chunk on each call.
    const status = this.add
      .text(0, 16, '', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        align: 'center',
        wordWrap: { width: width - 40 },
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);
    this.gateBannerStatus = status;

    this.gateBannerContainer.add([bg, title, status]);
    this.gateBannerContainer.setVisible(false);
  }

  private refreshGate(): void {
    const reason = this.mpBlockReason();

    // Buttons: enabled only when MP is unblocked entirely.
    for (const btn of this.mpButtons) btn.setEnabled(reason === null);

    if (!this.gateBannerContainer) return;
    if (!reason) {
      this.gateBannerContainer.setVisible(false);
      return;
    }
    this.gateBannerContainer.setVisible(true);

    const title = this.gateBannerContainer.getByName(
      'title'
    ) as Phaser.GameObjects.Text | null;
    const status = this.gateBannerStatus;

    if (reason === 'signin') {
      title?.setText('MULTIPLAYER LOCKED');
      status?.setText('Sign in to play with friends.');
      status?.setColor(COLORS.textSecondary);
    } else if (reason === 'confirm') {
      title?.setText('CONFIRM YOUR EMAIL');
      status?.setText('Click the link we sent you, then tap RESEND if needed.');
      status?.setColor(COLORS.textSecondary);
      this.attachResendAction();
    } else if (reason === 'username') {
      title?.setText('SETTING UP YOUR ACCOUNT');
      status?.setText('Just a moment…');
      status?.setColor(COLORS.textMuted);
    }
  }

  /**
   * Add a tappable "RESEND" link to the banner for the 'confirm'
   * state. Lives in its own container child so refreshGate() can
   * wipe + recreate without disturbing the title/status text. Idle
   * vs. submitting vs. sent states are surfaced inline in the link
   * label itself (cheaper than a separate spinner UI).
   */
  private attachResendAction(): void {
    if (!this.gateBannerContainer) return;
    // Wipe any prior resend action (e.g., from a previous render).
    const prior = this.gateBannerContainer.getByName('resend');
    if (prior) prior.destroy();

    const link = this.add
      .text(0, 42, 'RESEND', {
        fontFamily: FONT_HEADER,
        fontSize: '14px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);
    link.setName('resend');
    link.setInteractive({ useHandCursor: true });

    let busy = false;
    link.on('pointerup', async () => {
      if (busy) return;
      const email = getAuth().user?.email;
      if (!email) return;
      busy = true;
      link.setText('SENDING…');
      link.setColor(COLORS.accentAmberHex);
      const result = await resendConfirmation(email);
      if (result.ok) {
        link.setText('SENT — CHECK INBOX');
        link.setColor(COLORS.accentGreenHex);
        // Auto-reset back to the idle label after a few seconds so a
        // user who still doesn't see the email can try again without
        // having to refresh.
        this.time.delayedCall(4000, () => {
          if (!link.active) return;
          link.setText('RESEND');
          link.setColor(COLORS.accentCyanHex);
          busy = false;
        });
      } else {
        link.setText('FAILED — TAP TO RETRY');
        link.setColor(COLORS.accentRedHex);
        this.time.delayedCall(3000, () => {
          if (!link.active) return;
          link.setText('RESEND');
          link.setColor(COLORS.accentCyanHex);
          busy = false;
        });
      }
    });

    this.gateBannerContainer.add(link);
  }

  // --- Auth control (Slice 1.2a) ---

  /**
   * Small chip in the top-right corner that surfaces session state:
   *   - logged out                →  "SIGN IN"
   *   - logged in, no profile yet →  "EMAIL  ·  SIGN OUT" (transient)
   *   - logged in with profile    →  "USERNAME  ·  SIGN OUT"
   *
   * MP gating still TODO in Slice 1.2c — this is purely the entry /
   * exit affordance + identity readout. Re-renders on either
   * onAuthChange or onProfileChange.
   *
   * The profile-change listener ALSO drives the auto-route to
   * UsernameSetup if the fetch resolves to 'missing' after we've
   * already rendered Home.
   */
  private drawAuthControl(): void {
    // Container holds all auth-chip children so we can wipe + redraw
    // in one place when the session transitions.
    this.authContainer = this.add.container(0, 0);

    const refresh = (): void => {
      // Profile-side flip to 'missing' after initial render means the
      // user landed signed-in (e.g., post-confirmation), and we now
      // know they need to set a username. Bounce.
      if (this.shouldRouteToUsernameSetup()) {
        this.scene.start('UsernameSetup');
        return;
      }
      this.authContainer?.removeAll(true);
      this.renderAuthChip(getAuth(), getProfile());
      // Same listener drives the MP gate: signed-in/confirmed state
      // controls the banner copy + the two MP button states.
      this.refreshGate();
    };
    refresh();

    this.unsubscribeAuth = onAuthChange(() => refresh());
    this.unsubscribeProfile = onProfileChange(() => refresh());
  }

  private renderAuthChip(auth: AuthState, profile: ProfileState): void {
    if (!this.authContainer) return;
    const rightX = DESIGN_WIDTH - 30;
    const y = 80;

    if (!auth.user) {
      // Logged out — single tappable "SIGN IN" link in cyan.
      const label = this.add
        .text(rightX, y, 'SIGN IN', {
          fontFamily: FONT_HEADER,
          fontSize: '18px',
          color: COLORS.accentCyanHex,
          fontStyle: '700',
        })
        .setLetterSpacing(4)
        .setOrigin(1, 0.5);
      // Generous hit-area covers ≥56px high + the full text width
      // plus padding for a forgiving tap target.
      const hit = this.add
        .rectangle(rightX, y, label.width + 40, 56, 0x000000, 0)
        .setOrigin(1, 0.5)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.scene.start('Auth'));
      this.authContainer.add([label, hit]);
      return;
    }

    // Logged in — prefer username over email when the profile is
    // loaded. Falls back to email during the brief window between
    // sign-in and the profile fetch resolving.
    const identity =
      profile.kind === 'exists'
        ? profile.profile.username
        : (auth.user.email ?? '');
    const displayIdentity =
      identity.length > 22 ? identity.slice(0, 21) + '…' : identity;
    const identityIsUsername = profile.kind === 'exists';

    const signOutLabel = this.add
      .text(rightX, y, 'SIGN OUT', {
        fontFamily: FONT_HEADER,
        fontSize: '16px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(1, 0.5);
    const signOutHit = this.add
      .rectangle(rightX, y, signOutLabel.width + 32, 56, 0x000000, 0)
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    signOutHit.on('pointerup', () => {
      void signOut();
    });

    // Identity sits to the LEFT of the SIGN OUT link, separated by a
    // muted dot. Usernames render in header font / cyan (their own
    // identity); the email-fallback uses mono / muted (transient).
    const separator = this.add
      .text(rightX - signOutLabel.width - 12, y, '·', {
        fontFamily: FONT_MONO,
        fontSize: '16px',
        color: COLORS.textMuted,
      })
      .setOrigin(1, 0.5);
    const identityText = this.add
      .text(separator.x - separator.width - 8, y, displayIdentity, {
        fontFamily: identityIsUsername ? FONT_HEADER : FONT_MONO,
        fontSize: identityIsUsername ? '15px' : '14px',
        color: identityIsUsername
          ? COLORS.textPrimary
          : COLORS.textSecondary,
        fontStyle: identityIsUsername ? '700' : 'normal',
      })
      .setLetterSpacing(identityIsUsername ? 3 : 1)
      .setOrigin(1, 0.5);

    this.authContainer.add([
      identityText,
      separator,
      signOutLabel,
      signOutHit,
    ]);
  }

  // drawFooter() retired in MP-Lobby-2.0 A.4. The previous footer
  // (BATTLESHARK · version + "preview inbox (mock)" link) lived
  // where the bottom AppNav strip now sits. Version label is
  // available via the INFO tab in the Settings group; the preview
  // link is obsolete now that AppNav's 'games' tab leads to the
  // real inbox.
}
